import { createFileRoute } from '@tanstack/react-router';
import { Check, X } from "lucide-react";
import { ContentHeader, ContentSection, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/comparisons/aws-api-gateway')({
  component: AWSComparisonPage,
  head: () => ({
    meta: [
      { title: 'Matricks vs AWS API Gateway | Matricks' },
      { name: 'description', content: 'Compare Matricks and AWS API Gateway. Multi-cloud flexibility vs AWS-native integration.' },
    ],
  }),
});

const comparisonData = [
  { feature: "API Gateway", matricks: true, aws: true },
  { feature: "Lambda Integration", matricks: true, aws: true },
  { feature: "Multi-Cloud Support", matricks: true, aws: false },
  { feature: "Pre-built Integrations", matricks: "2000+", aws: "AWS Services" },
  { feature: "Visual Workflow Builder", matricks: true, aws: "Step Functions" },
  { feature: "AI/LLM Gateway", matricks: true, aws: false },
  { feature: "AI Agent Platform", matricks: true, aws: false },
  { feature: "Fine-Grained Policies", matricks: true, aws: "IAM" },
  { feature: "Cost Tracking", matricks: true, aws: "CloudWatch" },
  { feature: "No Vendor Lock-in", matricks: true, aws: false },
  { feature: "Unified Dashboard", matricks: true, aws: false },
];

const faqItems = [
  {
    question: "Can Matricks work with AWS services?",
    answer: "Yes, Matricks integrates with AWS services including Lambda, S3, DynamoDB, SQS, and more. You get AWS integration plus access to non-AWS services through one platform.",
  },
  {
    question: "How does pricing compare?",
    answer: "AWS API Gateway pricing is based on API calls and data transfer, which can become expensive at scale. Matricks offers predictable pricing with all features included. For most workloads, total cost is lower with Matricks.",
  },
  {
    question: "What about performance?",
    answer: "Both platforms offer low-latency API routing. Matricks adds <10ms overhead regardless of cloud provider. For AWS-heavy workloads, you won't notice a performance difference.",
  },
];

function AWSComparisonPage() {
  return (
    <div>
      <ContentHeader
        title="Matricks vs AWS API Gateway"
        description="AWS API Gateway is tightly integrated with AWS services. Here's how Matricks compares for teams wanting more flexibility."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Comparisons", href: "/comparisons" },
          { label: "vs AWS" },
        ]}
      />

      {/* Overview */}
      <ContentSection id="overview" title="Overview">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold text-lg">AWS API Gateway</h3>
            <p className="text-muted-foreground mt-2">
              AWS API Gateway is Amazon's managed API service. It's deeply integrated with Lambda,
              IAM, and other AWS services. Best for teams fully committed to the AWS ecosystem
              who don't need external integrations.
            </p>
          </div>
          <div className="rounded-xl border bg-primary/5 border-primary/20 p-6">
            <h3 className="font-semibold text-lg">Matricks</h3>
            <p className="text-muted-foreground mt-2">
              Matricks is a multi-cloud integration platform with 2000+ connectors. Works with
              AWS, Azure, GCP, and external services equally well. Best for teams wanting
              flexibility, AI capabilities, and no vendor lock-in.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* Comparison Table */}
      <ContentSection
        id="comparison"
        title="Feature Comparison"
        background="muted"
      >
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className="py-4 px-4 text-left font-semibold">Feature</th>
                <th className="py-4 px-4 text-center font-semibold bg-primary/5 text-primary">Matricks</th>
                <th className="py-4 px-4 text-center font-semibold">AWS API Gateway</th>
              </tr>
            </thead>
            <tbody>
              {comparisonData.map((row) => (
                <tr key={row.feature} className="border-b last:border-0">
                  <td className="py-4 px-4">{row.feature}</td>
                  <td className="py-4 px-4 text-center bg-primary/5">
                    {row.matricks === true ? (
                      <Check className="h-5 w-5 text-green-500 mx-auto" />
                    ) : row.matricks === false ? (
                      <X className="h-5 w-5 text-red-500 mx-auto" />
                    ) : (
                      <span className="text-sm">{row.matricks}</span>
                    )}
                  </td>
                  <td className="py-4 px-4 text-center">
                    {row.aws === true ? (
                      <Check className="h-5 w-5 text-green-500 mx-auto" />
                    ) : row.aws === false ? (
                      <X className="h-5 w-5 text-red-500 mx-auto" />
                    ) : (
                      <span className="text-sm text-muted-foreground">{row.aws}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ContentSection>

      {/* Key Differences */}
      <ContentSection id="differences" title="Key Differences">
        <div className="space-y-6">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Multi-Cloud Strategy</h3>
            <p className="text-muted-foreground mt-2">
              AWS API Gateway locks you into AWS. Matricks lets you connect to any service,
              anywhere. Essential for organizations with multi-cloud strategies or external
              partner integrations.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">External Integrations</h3>
            <p className="text-muted-foreground mt-2">
              AWS API Gateway excels at connecting AWS services but requires custom development
              for external APIs. Matricks provides 2000+ pre-built connectors for immediate
              integration with any service.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">AI Capabilities</h3>
            <p className="text-muted-foreground mt-2">
              Matricks includes AI/LLM gateway and agent platform. AWS offers Bedrock separately,
              but there's no unified platform for API and AI management. Matricks provides
              everything in one place.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Go Multi-Cloud with Matricks"
        description="Get the flexibility you need without sacrificing AWS integration."
        primaryAction={{ label: "Start Free Trial", href: "/login" }}
        secondaryAction={{ label: "Contact Sales", href: "/info/contact" }}
        background="primary"
      />
    </div>
  );
}
